/**
 * Koi Market — Koi Pharmacy category filter (unit 06).
 *
 * `.mockup/koi-pharmacy.html:532-546` · `.mockup/script.js:2691-2752`
 *
 * Client-side and instant, which is the whole reason this is not
 * `blocks/filters.liquid`: that one loads facets.js and re-requests the
 * collection, and a buyer working out what is wrong with a fish should not wait
 * on a round trip to compare two shelves.
 *
 * Every card is already in the DOM — each `_rx-category` block renders its
 * collection server-side — so filtering is only ever toggling `hidden`. That also
 * means the full range is indexable and the page works unfiltered with JS off.
 *
 * The pill row is built here from those blocks. The section cannot read theme
 * block settings in Liquid, so each block publishes its key, label and sellable
 * count as data attributes on its `[data-rx-group]` wrapper.
 *
 * Jump links elsewhere on the page (the category cards, and the treatment links
 * inside the anatomy panel) carry `data-rx-jump="<key>"` or `rx-cat--<key>`;
 * those are handled here too, so one filter has one owner.
 */

import { Component } from '@theme/component';

class RxFilter extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();

    this.chipList = this.querySelector('[data-rx-chips]');
    this.chipTemplate = this.querySelector('[data-rx-chip-template]');
    this.cards = Array.from(this.querySelectorAll('.rx-grid__item[data-rx-cat]'));
    this.countEl = this.querySelector('[data-rx-count]');
    this.emptyEl = this.querySelector('[data-rx-empty]');

    // Section setting "Show All chip"; when off, the grid opens on the first category.
    this.showAll = this.getAttribute('data-rx-show-all') !== 'false';

    this.buildChips();

    this.onClick = this.onClick.bind(this);
    this.onJump = this.onJump.bind(this);
    this.addEventListener('click', this.onClick);

    // Jump links live outside this element, so they are bound on the document.
    document.addEventListener('click', this.onJump);

    const firstKey = this.chips[0]?.getAttribute('data-rx-filter');
    this.apply(this.showAll || !firstKey ? 'all' : firstKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('click', this.onJump);
  }

  /**
   * One pill per category block, in block order, after "All" when it is shown.
   * Two blocks that share a key share one pill, so the key stays a single
   * filter target.
   */
  buildChips() {
    this.labels = new Map();
    this.chips = [];
    if (!this.chipList || !this.chipTemplate) return;

    const groups = new Map();
    for (const group of this.querySelectorAll('[data-rx-group]')) {
      const key = group.getAttribute('data-rx-key');
      if (!key) continue;
      const count = Number(group.getAttribute('data-rx-count')) || 0;
      const existing = groups.get(key);
      if (existing) {
        existing.count += count;
      } else {
        groups.set(key, { label: group.getAttribute('data-rx-label') || key, count });
      }
    }

    this.chipList.replaceChildren();
    const allLabel = this.getAttribute('data-rx-all-label') || 'All';
    if (this.showAll) this.addChip('all', allLabel, this.sellableIds('all').size);
    for (const [key, { label, count }] of groups) this.addChip(key, label, count);

    this.chipList.hidden = false;
    if (this.countEl) this.countEl.hidden = false;
  }

  addChip(key, label, count) {
    const item = this.chipTemplate.content.firstElementChild.cloneNode(true);
    const button = item.querySelector('button');
    button.setAttribute('data-rx-filter', key);
    item.querySelector('[data-rx-chip-label]').textContent = label;
    item.querySelector('[data-rx-chip-count]').textContent = String(count);
    this.chipList.append(item);
    this.chips.push(button);
    this.labels.set(key, label);
  }

  /** Product ids that are for sale under a key; "all" counts each product once. */
  sellableIds(key) {
    const ids = new Set();
    for (const card of this.cards) {
      if (key !== 'all' && card.getAttribute('data-rx-cat') !== key) continue;
      if (card.querySelector('.is-soon')) continue;
      ids.add(card.getAttribute('data-rx-product'));
    }
    return ids;
  }

  onClick(event) {
    const chip = event.target.closest('[data-rx-filter]');
    if (!chip || !this.contains(chip)) return;
    this.apply(chip.getAttribute('data-rx-filter'));
  }

  onJump(event) {
    // Two shapes of jump link, because they come from two kinds of markup.
    // The anatomy panel is purpose-built Liquid, so its treatment links carry a
    // real data-rx-jump. The category cards are stock group blocks, and
    // blocks/group.liquid exposes custom_class but no arbitrary data attribute —
    // so those are matched on the class name the card already has.
    const link = event.target.closest('[data-rx-jump], .rx-cat');
    if (!link) return;

    let key = link.getAttribute('data-rx-jump');
    if (!key) {
      const match = /(?:^|\s)rx-cat--([a-z0-9-]+)/.exec(link.className);
      if (!match) return;
      key = match[1];
    }
    // Only claim the click if this filter actually has that category; otherwise
    // leave the link to behave as the plain anchor it already is.
    if (!this.labels.has(key)) return;

    this.apply(key);
  }

  apply(key) {
    // `sellable` drives the count line so it agrees with the pill counts, which
    // skip coming-soon teasers. `visible` drives the empty state, because a
    // category holding only a teaser is not empty. Under "All" a product that
    // sits in two collections is shown once, at its first position.
    const seen = new Set();
    const sellable = new Set();
    let visible = 0;

    for (const card of this.cards) {
      const id = card.getAttribute('data-rx-product');
      let match = key === 'all' || card.getAttribute('data-rx-cat') === key;
      if (match && seen.has(id)) match = false;
      card.hidden = !match;
      if (!match) continue;
      seen.add(id);
      visible += 1;
      if (!card.querySelector('.is-soon')) sellable.add(id);
    }

    for (const chip of this.chips) {
      const on = chip.getAttribute('data-rx-filter') === key;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', String(on));
    }

    const label = this.labels.get(key) || key;

    if (this.countEl) {
      const template = this.countEl.getAttribute('data-rx-count-template') || '[count]';
      this.countEl.textContent = template
        .replace('[count]', String(sellable.size))
        .replace('[category]', label);
    }

    if (this.emptyEl) {
      const template = this.emptyEl.getAttribute('data-rx-empty-template') || '';
      this.emptyEl.textContent = template.replace('[category]', label);
      this.emptyEl.hidden = visible > 0;
    }
  }
}

if (!customElements.get('rx-filter')) {
  customElements.define('rx-filter', RxFilter);
}
